import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { useAuth } from '@/hooks/useAuth';
import type { RoleType } from '@/context/AuthContext';
import {
  type Capability,
  computeCapabilities,
  deriveRoleSet,
} from '@/lib/accessCapabilities';
import { LegacyAvatar } from '@/components/ui/LegacyAvatar';
import { CoBrand } from '@/components/ui/co-brand';
import { listInquiries } from '@/graphql/queries';
import { useStreamUnreadCount } from '@/lib/useStreamUnreadCount';
import { useIsBelowMd } from '@/lib/useMediaQuery';
import { isMeridianNewScreensEnabled, isMeritEnabled, isCommunityEnabled, isApplicationFormEnabled } from '@/config/featureFlags';
import { useApprovalsTabs } from '@/components/application-form/hooks/useApprovalsTabs';
import { renderNavRow, type SidebarNavRowItem } from './sidebar-nav-row';
import { SidebarNavGroup } from './sidebar-nav-group';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  CalendarDays,
  MessageSquare,
  MessagesSquare,
  Mail,
  BarChart3,
  Megaphone,
  Newspaper,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Crown,
  Trophy,
  TrendingUp,
  HeartHandshake,
  Wallet,
  Activity,
  ClipboardCheck,
  ClipboardList,
  Gift,
  Shirt,
  X,
} from 'lucide-react';

/**
 * Two admin-side portals share this sidebar:
 *   - Director   (/admin/*)      — dashboards, contestants, calendar, documents, approvals, messages
 *   - Spotlight  (/spotlight/*)  — audience insights (Insight + Program Impact)
 *
 * The sidebar detects the active portal from the current URL and renders the
 * matching nav items. The bottom button toggles between the two.
 */
export type PortalKey = 'director' | 'spotlight';

interface MenuItem {
  name: string;
  /** Optional display label; `name` stays the stable key for nav/subpage logic. */
  label?: string;
  path: string;
  icon: typeof LayoutDashboard;
  portal: PortalKey;
}

const menuItems: MenuItem[] = [
  // Director portal
  { name: 'Dashboard',    path: '/admin',              icon: LayoutDashboard, portal: 'director'  },
  { name: 'Bulletin',     path: '/admin/herald',       icon: Newspaper,       portal: 'director'  },
  { name: 'Community',  path: '/admin/community',    icon: Users,           portal: 'director'  },
  { name: 'Calendar',     path: '/admin/calendar',     icon: CalendarDays,    portal: 'director'  },
  { name: 'Approvals',    label: 'Applications', path: '/admin/approvals',    icon: ShieldCheck,     portal: 'director'  },
  { name: 'Podium',       path: '/admin/messages',     icon: MessageSquare,   portal: 'director'  },
  // The Document Center ("Documents") is no longer a top-level Director nav
  // item — it now lives inside admin Settings as a subpage (reached via
  // Settings → Documents). The `/admin/documents` route is retained for deep
  // links; only the sidebar entry was removed.
  // Meridian-redesign flag-gated entries (Req 13.4, 13.6). These four
  // names are added to `ROLE_NAV_ITEMS` below, but they only render
  // when `isMeridianNewScreensEnabled()` returns true. Order is
  // appended after the pre-redesign Director items so existing item
  // ordering is preserved verbatim (Property 5 admin slice).
  // Envoy uses Megaphone (icon parity with portal Sidebar — the same
  // sponsor-obligation concept must carry the same icon across roles).
  { name: 'Merit',  path: '/admin/merit',  icon: Trophy,         portal: 'director' },
  { name: 'Envoy',  path: '/admin/envoy',  icon: Megaphone,      portal: 'director' },
  { name: 'Patron', path: '/admin/patron', icon: HeartHandshake, portal: 'director' },
  { name: 'Vault',  path: '/admin/vault',  icon: Wallet,         portal: 'director' },
  // The Application Form builder ("Forms") is no longer a top-level Director
  // nav item — it now lives inside admin Settings as a subpage (reached via
  // Settings → Forms). The `/admin/application-builder` route is retained for
  // deep links; only the sidebar entry was removed.
  // Program Impact carries TrendingUp (impact growth / metrics
  // trending) because Megaphone is now Envoy's canonical icon
  // (post-(m.6) Phase 2.5 icon-parity sweep with portal Sidebar)
  // and HeartHandshake belongs to Patron (sponsor partnership).
  { name: 'Insight',        path: '/spotlight/social-metrics',  icon: BarChart3,  portal: 'spotlight' },
  { name: 'Program Impact', path: '/spotlight/program-impact', icon: TrendingUp, portal: 'spotlight' },
];

/**
 * Each nav item's destination is reachable iff the user holds the named
 * Capability the destination requires (access-roles-unification Req 5.3, 6.x).
 * Visibility is therefore driven from the user's `capabilities` via `can()` —
 * never from `User.role` (Req 11.4). The mapping mirrors the design's
 * authoritative route→capability migration map:
 *   - dashboard / community / messages  → canViewAdminDashboard
 *   - calendar                           → canJudge
 *   - documents / approvals / herald /
 *     merit / envoy / patron / vault /
 *     spotlight (insight, impact)        → canManageUsers
 *
 * Spotlight and the meridian admin surfaces map to `canManageUsers` to
 * preserve their existing admin-only gate (design §migration map, note ²).
 */
const NAV_ITEM_CAPABILITY: Readonly<Record<string, Capability>> = {
  Dashboard: 'canViewAdminDashboard',
  Community: 'canViewAdminDashboard',
  Podium: 'canViewAdminDashboard',
  Calendar: 'canJudge',
  Bulletin: 'canManageUsers',
  Approvals: 'canManageUsers',
  Merit: 'canManageUsers',
  Envoy: 'canManageUsers',
  Patron: 'canManageUsers',
  Vault: 'canManageUsers',
  Insight: 'canManageUsers',
  'Program Impact': 'canManageUsers',
};

/**
 * Names of nav items added by the meridian-redesign spec that are
 * gated behind a feature flag. When the gating flag is off these
 * items are filtered out before rendering, regardless of role
 * membership in `ROLE_NAV_ITEMS`. When on, role-based filtering still
 * applies via `ROLE_NAV_ITEMS` — Patron/Envoy/Vault are admin-only
 * per Requirement 13.6, while Merit additionally surfaces for judges.
 *
 * Gating flag per item (decoupled — see `isFlagGatedItemEnabled`):
 *   - Merit                 → `isMeritEnabled()` (merit-only flag, so
 *                             Merit can ship to prod independently)
 *   - Envoy / Patron / Vault → `isMeridianNewScreensEnabled()`
 *   - Community             → `isCommunityEnabled()` (community-only
 *                             flag, `VITE_FEATURE_COMMUNITY`). The
 *                             Community Contacts nav item must NOT
 *                             register unless the flag is strictly
 *                             "true", matching the flag-gated
 *                             `/admin/community` route registration in
 *                             App.tsx (community-contacts task 7.3,
 *                             Requirement 16.2).
 *
 * Validates: Requirements 13.4, 13.6, 16.2.
 */
const FLAG_GATED_NAV_ITEMS: ReadonlySet<string> = new Set([
  'Merit',
  'Envoy',
  'Patron',
  'Vault',
  'Community',
]);

/**
 * Names of items hoisted to the lower action group (above Settings)
 * rather than rendered in the main nav. Documents used to live here,
 * but it was relocated into admin Settings as a subpage, so the lower
 * group is currently empty. The partition mechanism is retained for
 * any future utility destination.
 */
const LOWER_GROUP_ITEMS: ReadonlySet<string> = new Set([]);

/**
 * Sub-page declarations per parent product (PR (m.6) Phase 2.6).
 *
 * Each parent product with 2+ sub-pages renders as a
 * `<SidebarNavGroup>` collapsible carrot in the sidebar AND a
 * `<PillTabs>` strip at the top of its parent screen. The two
 * surfaces share the same labels so the user has a single mental
 * model.
 *
 * Labels follow the 12-char rule — every label is a 1-2 word
 * direct destination name. Long-form labels ("Tabulation
 * Dashboard", "Scholarships & Endowments", "Email Templates")
 * are intentionally trimmed.
 *
 * Path = `<parent-route>?tab=<id>`. The id is the canonical sub-page
 * key consumed by useSearchParams in the parent screen.
 */
export interface SubPageDef extends SidebarNavRowItem {
  /** Tab id for `useSearchParams.get('tab')` matching. */
  tab: string;
}

/**
 * The Approvals sub-tab set is now DERIVED from the Active_Form's
 * approval-requiring pages via `useApprovalsTabs()` (approval-rollup-surfaces
 * Req 1.7, 5.1, 5.2, 7.5, 7.6), superseding the fixed `approvalsSubpages(flag)`
 * set. The hook returns icon-free `ApprovalsEntry` descriptors; the sidebar
 * layers its own icon per entry here. Since derived tab ids are per-org page
 * keys (not a closed union), the sidebar maps a small set of known ids to their
 * canonical icon and falls back to a generic default for any other derived tab.
 */
const APPROVALS_ENTRY_ICONS: Readonly<Record<string, typeof LayoutDashboard>> = {
  applications: ClipboardList,
  'approved-titles': Crown,
  costume: Shirt,
};

/** Generic icon for any derived Rollup_Tab without a known-id mapping. */
const DEFAULT_APPROVALS_ICON = ClipboardCheck;

const PODIUM_ADMIN_SUBPAGES: ReadonlyArray<SubPageDef> = [
  { name: 'Chat',      tab: 'chat',      path: '/admin/messages?tab=chat',      icon: MessagesSquare },
  { name: 'Inquiries', tab: 'inquiries', path: '/admin/messages?tab=inquiries', icon: Mail },
];

const MERIT_ADMIN_SUBPAGES: ReadonlyArray<SubPageDef> = [
  { name: 'Setup',   tab: 'setup',   path: '/admin/merit',         icon: Settings },
  { name: 'Monitor', tab: 'monitor', path: '/admin/merit/monitor', icon: BarChart3 },
];

const VAULT_ADMIN_SUBPAGES: ReadonlyArray<SubPageDef> = [
  { name: 'Review', tab: 'review', path: '/admin/vault?tab=review', icon: ClipboardCheck },
  { name: 'Awards', tab: 'awards', path: '/admin/vault?tab=awards', icon: Gift },
];

const SPOTLIGHT_SUBPAGES: ReadonlyArray<SubPageDef> = [
  { name: 'Insight', tab: 'insight', path: '/spotlight/social-metrics',  icon: Activity },
  { name: 'Impact',  tab: 'impact',  path: '/spotlight/program-impact',  icon: TrendingUp },
];

/**
 * Names of parent products that render as a SidebarNavGroup (carrot
 * expander). Used by the filter to remove their items from the flat
 * main-nav render — they're rendered as groups below.
 */
const GROUP_PARENT_NAMES: ReadonlySet<string> = new Set([
  'Approvals',
  'Podium',
  'Merit',
  'Vault',
  'Spotlight',
]);

/**
 * Names of items that are sub-page children of a group; filtered out
 * of the top-level render because they only appear nested inside
 * their parent group's expanded list. (Spotlight children — Insight
 * + Program Impact — fall into this category since Spotlight itself
 * is rendered as a synthetic group; the children are not in
 * `menuItems` for the other products since the sub-pages are only
 * declared in *_SUBPAGES tables above.)
 */
const SPOTLIGHT_CHILD_NAMES: ReadonlySet<string> = new Set([
  'Insight',
  'Program Impact',
]);

const SPOTLIGHT_GROUP_NAME = 'Spotlight';

/**
 * Maps each RoleType to the set of sidebar navigation item names visible to that role.
 * Exported for property-based testing (Property 7: Sidebar-Route Consistency).
 *
 * NOTE (access-roles-unification task 5.1): item visibility is now driven by
 * `NAV_ITEM_CAPABILITY` + `can()` (capabilities), never by `User.role`. This
 * table is kept as the documented, test-referenced projection of that
 * capability gating for the single-hat case (each row equals the items whose
 * required Capability the role's lone Access_Hat confers). `judge` therefore no
 * longer lists `Merit`: a judge holds only `canJudge`, and `/admin/merit` is an
 * admin-only (`canManageUsers`) surface, so linking it for judges would point
 * to a route they cannot open (Sidebar-Route Consistency).
 *
 * The four trailing entries on `admin` (Merit / Envoy /
 * Patron / Vault) are flag-gated by `FLAG_GATED_NAV_ITEMS`; they are
 * filtered out at render time when `isMeridianNewScreensEnabled()`
 * returns false (Requirements 13.4, 13.6).
 *
 * Documents lives in `ROLE_NAV_ITEMS` as before (Property 5 / 7
 * snapshots reference it by name) but renders in the lower action
 * group rather than the main nav per LOWER_GROUP_ITEMS.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 13.4, 13.6
 */
export const ROLE_NAV_ITEMS: Record<RoleType, string[]> = {
  admin: ['Dashboard', 'Bulletin', 'Community', 'Calendar', 'Approvals', 'Podium', 'Insight', 'Program Impact', 'Merit', 'Envoy', 'Patron', 'Vault'],
  poweruser: ['Dashboard', 'Community', 'Calendar', 'Podium'],
  // 'Community' was removed from `judge` when the nav item was
  // repointed from `/admin/participants` (admin/poweruser/judge) to
  // the flag-gated `/admin/community` directory, which is guarded by
  // `requiredRoles={['admin','poweruser']}` in App.tsx (community-
  // identity spec, task 6.1). Leaving 'Community' here would surface a
  // nav link to a route judges cannot open, violating Property 7
  // (Sidebar-Route Consistency). The `/admin/participants` route still
  // exists for judges; it is simply no longer linked from the sidebar.
  // 'Merit' was removed by the access-roles-unification spec (task 5.1):
  // it gates on `canManageUsers`, which a judge does not hold.
  judge: ['Calendar'],
  contestant: [],
};

/**
 * Human-readable role labels displayed beneath the user's name in the sidebar.
 *
 * Requirements: 10.5
 */
const ROLE_LABELS: Record<RoleType, string> = {
  admin: 'Admin',
  poweruser: 'Power User',
  judge: 'Judge',
  contestant: 'Contestant',
};

export interface AdminSidebarProps {
  /**
   * Whether the mobile drawer is currently open. Below the `md`
   * breakpoint the sidebar is hidden by default and slides in from
   * the left when this prop is `true`. At `md` and above the prop
   * is ignored — the sidebar is always visible.
   */
  mobileOpen?: boolean;
  /**
   * Callback fired when the user closes the mobile drawer (via the
   * X button, backdrop tap, or route change). Owned by the parent
   * `AdminLayout` so it can sync the layout's hamburger state.
   */
  onMobileClose?: () => void;
}

export default function AdminSidebar({ mobileOpen = false, onMobileClose }: AdminSidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, can, signOut } = useAuth();
  const userEmail = user?.email || '';
  // Capability gate (access-roles-unification Req 5.3, 5.4, 11.4): nav-item
  // visibility is driven from the user's capabilities — never from User.role.
  // Prefer the AuthContext `can()`; fall back to deriving capabilities from the
  // user's Cognito groups for callers/tests that predate the `can()` addition
  // (the migration is deliberately incremental and non-breaking — Req 13/14).
  const hasCapability = (capability: Capability): boolean =>
    typeof can === 'function'
      ? can(capability)
      : computeCapabilities(deriveRoleSet(user?.groups ?? [])).has(capability);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('idaho-pageant-admin-sidebar-collapsed');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // PR (k): below the `md` breakpoint (< 768px) the sidebar is now
  // hidden by default and revealed via a hamburger toggle in the
  // mobile header (PR (b2)). When `mobileOpen` is true the sidebar
  // slides in from the left as a 280px-wide drawer with a backdrop.
  // The user's persisted collapse preference (`isCollapsed`) is
  // ignored on mobile because the icon-only collapsed view is no
  // longer the mobile experience — mobile sees either the full
  // drawer (open) or zero nav (closed). At `md` and above the
  // sidebar is always visible and the user's collapse preference
  // applies.
  const isBelowMd = useIsBelowMd();
  const renderCollapsed = !isBelowMd && isCollapsed;

  // Auto-close the mobile drawer on route change so tapping a nav
  // item dismisses it. Skip the close callback if it's not provided
  // (e.g. when the AdminSidebar is rendered standalone outside the
  // AdminLayout shell).
  useEffect(() => {
    if (isBelowMd && mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const displayName = user?.fullName || user?.email || 'Admin';

  // PR (m.6) Phase 2: portal partitioning removed for admins. The
  // earlier model gated each item to its `portal` field (Director vs
  // Spotlight) and required a portal-switch button at the bottom of
  // the sidebar to toggle between them. Spec-owner directive merges
  // both portals into a single flat nav for admins so the user sees
  // every action they can take in one list. Non-admin roles
  // (poweruser, judge) continue to see only their allowed items —
  // visibility is now derived from `can()` capabilities (access-roles-
  // unification task 5.1) rather than a per-role allow-list. The
  // `portal` field on each item is retained for future contextual
  // grouping (e.g. visual section headers) but no longer drives item
  // visibility.
  // Independent flags gate the flag-gated nav items. Merit was
  // decoupled (merit-only flag) so it can ship to production without
  // exposing Envoy/Patron/Vault, which remain on the original meridian
  // flag. Community is gated independently by `VITE_FEATURE_COMMUNITY`
  // so the Community Contacts surfaces ship on their own cadence.
  const meridianEnabled = isMeridianNewScreensEnabled();
  const meritEnabled = isMeritEnabled();
  const communityEnabled = isCommunityEnabled();
  const applicationFormEnabled = isApplicationFormEnabled();
  // Derived Approvals sub-tabs (approval-rollup-surfaces Req 1.7, 5.1, 5.2,
  // 7.5, 7.6): one read-only Rollup_Tab per approval-requiring FormPage on the
  // Active_Form, gated on the Application_Form_Flag AND `canManageUsers` inside
  // the hook's pure deriver. While the pages are still loading we show only the
  // constant, retained Applications entry; the derived roll-up tabs appear once
  // the pages resolve.
  const { tabs: approvalsTabs, loading: approvalsLoading } = useApprovalsTabs();
  const approvalsSubPageDefs: ReadonlyArray<SubPageDef> = (
    approvalsLoading ? approvalsTabs.filter((entry) => !entry.derived) : approvalsTabs
  ).map((entry) => ({
    name: entry.label,
    tab: entry.tab,
    path: entry.path,
    icon: APPROVALS_ENTRY_ICONS[entry.tab] ?? DEFAULT_APPROVALS_ICON,
  }));
  // Judge Merit entry (merit-web-judging R1.3): a provisioned judge reaches the
  // Judge_Merit_View at `/merit/judge`, NOT the admin `/admin/merit` Setup/Monitor
  // surfaces. The admin "Merit" group above stays `canManageUsers`-gated; this is
  // the judge counterpart. Shown only to a PURE judge — holds `canJudge` but not
  // `canViewAdminDashboard` — so admins (who use the Setup group) and powerusers
  // (who hold canViewAdminDashboard but no Merit access) never see it. Gated by
  // the same `isMeritEnabled()` flag as the admin Merit item. The link is
  // route-consistent: `/merit/judge` gates on `canJudge`, which a judge holds.
  const showJudgeMerit =
    meritEnabled && hasCapability('canJudge') && !hasCapability('canViewAdminDashboard');
  // Per-item gating predicate for the flag-gated nav items. Merit is
  // gated by `isMeritEnabled()`; Community by `isCommunityEnabled()`;
  // Envoy/Patron/Vault by `isMeridianNewScreensEnabled()`. Items not in
  // `FLAG_GATED_NAV_ITEMS` are never gated here.
  const isFlagGatedItemEnabled = (name: string): boolean =>
    name === 'Merit'
      ? meritEnabled
      : name === 'Community'
        ? communityEnabled
        : name === 'Forms'
          ? applicationFormEnabled
          : meridianEnabled;
  // An item is shown iff the user holds the Capability its destination
  // requires (access-roles-unification Req 5.3, 11.4). Items not present in
  // `NAV_ITEM_CAPABILITY` fall back to the legacy per-role allow-list so any
  // unmapped item preserves its prior behavior.
  const isItemPermitted = (name: string): boolean => {
    const capability = NAV_ITEM_CAPABILITY[name];
    return capability !== undefined
      ? hasCapability(capability)
      : (ROLE_NAV_ITEMS[role] ?? []).includes(name);
  };
  // PR (m.6) Phase 2.6: items split into render groups, with each
  // parent product that has 2+ sub-pages rendered as a
  // SidebarNavGroup (carrot expander) rather than a flat row.
  //   - mainTop      — flat rows above any group (Dashboard,
  //                    Contestants, Calendar)
  //   - groupParents — Approvals, Podium, Merit, Vault, Spotlight
  //                    rendered as carrot expanders
  //   - mainBottom   — flat rows after the groups (Patron, Envoy)
  //   - lowerItems   — Documents (in the footer above Settings)
  //   - spotlightChildNames — filtered out of all flat renders;
  //                           only appear nested in Spotlight group
  const allFiltered = menuItems.filter(
    (item) =>
      isItemPermitted(item.name) &&
      (!FLAG_GATED_NAV_ITEMS.has(item.name) || isFlagGatedItemEnabled(item.name)),
  );
  const lowerItems = allFiltered.filter((item) => LOWER_GROUP_ITEMS.has(item.name));
  const spotlightChildren = allFiltered.filter((item) =>
    SPOTLIGHT_CHILD_NAMES.has(item.name),
  );

  // Items eligible to render in the flat main nav (not lower-group,
  // not spotlight-children, not group-parents). Inline filter below
  // — kept here for the explanatory grouping comment above.

  /**
   * Look up the sub-pages for a given group parent. Returns null
   * for groups whose role-filtered sub-page list is empty (which
   * downgrades the parent to a flat row in the render below).
   */
  function subPagesFor(groupName: string): ReadonlyArray<SubPageDef> | null {
    switch (groupName) {
      case 'Approvals':
        return approvalsSubPageDefs;
      case 'Podium':
        return PODIUM_ADMIN_SUBPAGES;
      case 'Merit':
        return MERIT_ADMIN_SUBPAGES;
      case 'Vault':
        return VAULT_ADMIN_SUBPAGES;
      case 'Spotlight':
        // Synthetic — Spotlight has no entry in `menuItems`. We
        // render it as a group when at least one Spotlight child
        // is in the admin's allow-list (Insight + Program Impact).
        return spotlightChildren.length > 0 ? SPOTLIGHT_SUBPAGES : null;
      default:
        return null;
    }
  }

  /**
   * Single-expanded-group state (PR (m.6) Phase 2.6.1).
   *
   * The user expects only one carrot to be open at a time. Click a
   * different chevron and the previously-open one closes. Auto-
   * expansion follows the URL: when the active route is inside a
   * group, that group is the open one.
   *
   * State derivation:
   *  - active route inside group X            → X is open
   *  - active route NOT inside any group      → no group is open
   *                                              UNLESS the user
   *                                              explicitly clicked
   *                                              a chevron (manual
   *                                              override carries
   *                                              until they navigate
   *                                              to a different
   *                                              group's route)
   */
  function activeGroupFromPath(): string | null {
    if (location.pathname.startsWith('/spotlight')) return 'Spotlight';
    for (const name of ['Approvals', 'Podium', 'Merit', 'Vault'] as const) {
      const item = menuItems.find((i) => i.name === name);
      if (item && location.pathname.startsWith(item.path)) return name;
    }
    return null;
  }
  const activeGroupName = activeGroupFromPath();
  // Sentinel value used in `manuallyOpenGroup` to represent "user
  // explicitly closed the URL-derived active group". The plain
  // `null` slot is reserved for "no manual override; URL-derived
  // rule wins". Without this distinction a click on the expanded
  // parent would set state to null, the URL-derived rule would
  // immediately re-open the same group on the next render (because
  // navigation lands inside that group's prefix), and the user
  // would observe the group flicker open again — i.e. the user-
  // facing bug reported in contestant-admin-ui-polish #18.
  //
  // Validates: contestant-admin-ui-polish Requirements 18.1, 18.4.
  const NONE_SENTINEL = '__none__';
  const [manuallyOpenGroup, setManuallyOpenGroup] = useState<string | null>(null);
  // Reset manual override whenever the active route changes. Two
  // cases:
  //   1. Plain manual override (`prev` is a group name): clear it
  //      so the URL-derived rule wins on navigation, matching the
  //      pre-(14.9) behavior.
  //   2. Sentinel "user closed this" (`prev` is NONE_SENTINEL):
  //      preserve the sentinel as long as the user is still within
  //      a group's prefix (e.g. they clicked the expanded Approvals
  //      parent and got navigated to /admin/approvals — same group).
  //      Once they navigate OUTSIDE any group prefix the sentinel
  //      resets to null because there's no longer a group to keep
  //      forcibly closed.
  useEffect(() => {
    setManuallyOpenGroup((prev) => {
      if (prev === NONE_SENTINEL) {
        return activeGroupFromPath() === null ? null : NONE_SENTINEL;
      }
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const openGroupName =
    manuallyOpenGroup === NONE_SENTINEL
      ? null
      : (manuallyOpenGroup ?? activeGroupName);

  function handleGroupToggle(groupName: string): void {
    setManuallyOpenGroup((prev) => {
      // Clicking the chevron / parent of the currently-open group
      // closes it; clicking a different group opens that one
      // (closing whichever was previously open).
      const currentlyOpen =
        prev === NONE_SENTINEL ? null : (prev ?? activeGroupName);
      if (currentlyOpen === groupName) return NONE_SENTINEL;
      return groupName;
    });
  }

  // Fetch profilePhotoS3Key from the User table — the single source of
  // truth after avatar-ssot-cleanup. The old Cognito `custom:profile
  // PhotoS3Key` fallback was removed once the backfill copied every
  // admin's key into the User row.
  const [profilePhotoS3Key, setProfilePhotoS3Key] = useState<string | null>(null);
  useEffect(() => {
    if (!user?.cognitoSub) return;
    async function fetchProfilePhoto() {
      try {
        const adminClient = generateClient();
        const userResult = await adminClient.graphql({
          query: `query($cognitoSub: String!) { getUser(cognitoSub: $cognitoSub) { profilePhotoS3Key } }`,
          variables: { cognitoSub: user!.cognitoSub },
          authMode: 'userPool',
        }) as GraphQLResult<{ getUser: { profilePhotoS3Key?: string } | null }>;
        if (userResult.data?.getUser?.profilePhotoS3Key) {
          setProfilePhotoS3Key(userResult.data.getUser.profilePhotoS3Key);
        }
      } catch {
        // Silently fail — Avatar will use initial fallback
      }
    }
    fetchProfilePhoto();
  }, [user?.cognitoSub, user?.email]);

  // Unread indicator for admins = unread chat (via Stream) OR unread inquiries
  // (via a lightweight GraphQL poll). The chat signal is event-driven from
  // Stream; only the inquiries side requires a timer.
  const { hasUnread: hasUnreadChat } = useStreamUnreadCount();
  const [hasUnreadInquiries, setHasUnreadInquiries] = useState(false);
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    const sidebarMsgClient = generateClient();
    const checkInquiries = async () => {
      try {
        const inqResult = (await sidebarMsgClient.graphql({
          query: listInquiries,
          variables: { limit: 200 },
        })) as GraphQLResult<{
          listInquiries: { items: Array<{ id: string; status: string }> };
        }>;
        const items = inqResult.data?.listInquiries?.items ?? [];
        const count = items.filter((inq) => inq.status === 'UNREAD').length;
        if (!cancelled) setHasUnreadInquiries(count > 0);
      } catch {
        // leave previous value in place
      }
    };
    checkInquiries();
    const interval = setInterval(checkInquiries, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userEmail]);
  const hasUnread = hasUnreadChat || hasUnreadInquiries;

  return (
    <aside
      data-testid="admin-sidebar"
      data-mobile-open={isBelowMd ? mobileOpen : undefined}
      className={`flex-shrink-0 z-40 bg-sidebar border-r border-border flex flex-col h-screen transition-all duration-300 ease-in-out ${
        isBelowMd
          ? `fixed inset-y-0 left-0 w-[280px] shadow-prominent ${
              mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
            }`
          : `relative sticky top-0 ${renderCollapsed ? 'w-20' : 'w-64'}`
      }`}
    >
      {/*
        Mobile close (X) button — visible only below `md`. Lets the
        user dismiss the drawer without backdrop-tapping or
        navigating. Mirrors the mockup at
        `reignara_redesign/app/dashboard/page.tsx`.
      */}
      {isBelowMd && (
        <button
          type="button"
          onClick={() => onMobileClose?.()}
          aria-label="Close navigation"
          data-testid="admin-sidebar-mobile-close"
          className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition-colors bg-transparent border-none cursor-pointer touch-target z-10"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/*
        Collapse / expand button — see Sidebar.tsx for rationale. The
        outer button is 32×32 with a `before:` 6px hit-test halo; the
        visible pill is the inner span. Same affordance as the portal
        sidebar, themed via tokens.
      */}
      <button
        type="button"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        onClick={() => { const next = !isCollapsed; setIsCollapsed(next); try { localStorage.setItem('idaho-pageant-admin-sidebar-collapsed', JSON.stringify(next)); } catch {} }}
        disabled={isBelowMd}
        className={`absolute -right-4 top-[5.5rem] w-8 h-8 flex items-center justify-center z-50 group bg-transparent border-none p-0 before:content-[''] before:absolute before:-inset-1.5 ${isBelowMd ? 'cursor-not-allowed opacity-0 pointer-events-none' : 'cursor-pointer'}`}
      >
        <span className="w-6 h-6 bg-foreground text-background rounded-full flex items-center justify-center shadow-raised group-hover:bg-foreground/80 group-active:scale-95 transition-all">
          {renderCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </span>
      </button>

      <div className="p-8 border-b border-border">
        <div className="flex flex-col items-center gap-3 mb-2">
          <LegacyAvatar
            profilePhotoS3Key={profilePhotoS3Key}
            fullName={displayName}
            size="md"
            variant="admin"
            shape="rounded"
            className={
              renderCollapsed
                ? '!w-14 !h-14 !min-w-[3.5rem] shrink-0 transition-all duration-300'
                : '!w-20 !h-20 !min-w-[5rem] shrink-0 transition-all duration-300'
            }
          />
          {!renderCollapsed && (
            <div className="min-w-0 w-full text-center">
              <h3 className="font-display text-2xl text-foreground leading-tight truncate">
                {displayName}
              </h3>
              <span className="text-eyebrow-sm text-primary truncate flex items-center justify-center gap-1 mt-1">
                <Crown className="w-3 h-3 shrink-0 inline-block" />
                {ROLE_LABELS[role] ?? 'Admin'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Active-tenant switcher (tenant-foundation Task 11) — the Slack-style
          workspace switcher. Quiet with a single Membership, a dropdown with
          more; hidden in the icon-only collapsed rail. */}
      {!renderCollapsed && (
        <div className="px-4 pt-4">
          <TenantSwitcher />
        </div>
      )}

      <nav className="flex-grow min-h-0 p-4 space-y-2 mt-4 overflow-y-auto admin-sidebar-nav">
        {/* PR (m.6) Phase 2.6: render each item as either a flat
            row OR a SidebarNavGroup (carrot expander). The
            iteration order follows `allFiltered` exactly so item
            ordering matches the legacy Property 5 snapshot. */}
        {allFiltered.flatMap((item) => {
          if (LOWER_GROUP_ITEMS.has(item.name)) return [];
          if (SPOTLIGHT_CHILD_NAMES.has(item.name)) return [];

          if (GROUP_PARENT_NAMES.has(item.name)) {
            const subPages = subPagesFor(item.name);
            if (subPages && subPages.length >= 2) {
              const indicator = item.name === 'Podium' && hasUnread;
              return [
                <SidebarNavGroup
                  key={`group-${item.name}`}
                  name={item.name}
                  icon={item.icon}
                  defaultPath={item.path}
                  activePathPrefix={item.path}
                  children={subPages}
                  location={location}
                  renderCollapsed={renderCollapsed}
                  testIdPrefix={`admin-sidebar-${item.name.toLowerCase()}-group`}
                  showIndicatorDot={indicator}
                  isOpen={openGroupName === item.name}
                  onToggle={() => handleGroupToggle(item.name)}
                  onNavClick={isBelowMd ? () => onMobileClose?.() : undefined}
                />,
              ];
            }
          }

          // Flat row.
          return [
            renderNavRow(item, {
              location,
              renderCollapsed,
              hasUnread,
              onNavClick: isBelowMd ? () => onMobileClose?.() : undefined,
            }),
          ];
        })}

        {/* Judge Merit entry (merit-web-judging R1.3) — the judge
            counterpart to the admin Merit group above. Rendered
            explicitly (not via `menuItems`/`ROLE_NAV_ITEMS`) because
            it points a PURE judge at the Judge_Merit_View
            (`/merit/judge`), a different destination from the admin
            `/admin/merit` Setup/Monitor surfaces. `showJudgeMerit`
            confines it to judges (canJudge ∧ ¬canViewAdminDashboard)
            under the merit flag. */}
        {showJudgeMerit &&
          renderNavRow(
            { name: 'Merit', path: '/merit/judge', icon: Trophy },
            {
              location,
              renderCollapsed,
              onNavClick: isBelowMd ? () => onMobileClose?.() : undefined,
            },
          )}

        {/* Spotlight is synthetic — it has no entry in `menuItems` so
            we render it explicitly when at least one Spotlight child
            is in the admin's allow-list. The two children appear
            as nested rows under the Spotlight parent group. The
            parent's defaultPath is `/spotlight` which the App.tsx
            index route redirects to `/spotlight/social-metrics`. */}
        {spotlightChildren.length > 0 && (
          <SidebarNavGroup
            key="group-spotlight"
            name={SPOTLIGHT_GROUP_NAME}
            icon={BarChart3}
            defaultPath="/spotlight"
            activePathPrefix="/spotlight"
            children={SPOTLIGHT_SUBPAGES}
            location={location}
            renderCollapsed={renderCollapsed}
            testIdPrefix="admin-sidebar-spotlight-group"
            isOpen={openGroupName === SPOTLIGHT_GROUP_NAME}
            onToggle={() => handleGroupToggle(SPOTLIGHT_GROUP_NAME)}
            onNavClick={isBelowMd ? () => onMobileClose?.() : undefined}
          />
        )}

        {/* Section break before the utility / account block.
            Pinned at the bottom of the sidebar via `mt-auto` on the
            sibling `<div>` below the scrollable `<nav>` (matches
            the portal Sidebar.tsx pattern). On mobile the entire
            `<aside>` is full-height and scrolls as a drawer, so
            `mt-auto` still produces the right layout — the utility
            block lives below the main nav and the `<aside>`'s
            `h-screen` ensures `mt-auto` pushes it to the bottom of
            the viewport when the content is shorter than the
            screen. No mobile-specific handling needed. */}
      </nav>
      <div className="p-4 pt-4 mt-auto border-t border-border space-y-2 shrink-0">
        {/* Lower group — utility destinations (Documents) hoisted out
            of the main nav per PR (m.6) Phase 2.5 spec-owner direction. */}
        {lowerItems.map((item) => renderNavRow(item, {
          location,
          renderCollapsed,
          hasUnread,
          tone: 'muted',
          onNavClick: isBelowMd ? () => onMobileClose?.() : undefined,
        }))}
        <Link
          to="/admin/settings"
          onClick={() => { if (isBelowMd) onMobileClose?.(); }}
          className="flex items-center gap-4 px-4 py-3 min-h-[var(--touch-target-min)] text-muted-foreground hover:text-foreground transition-colors text-nav-item group relative"
        >
          <Settings className="w-4 h-4 shrink-0" />
          {!renderCollapsed && <span>Settings</span>}
        </Link>
        <button
          onClick={async () => { await signOut(); navigate('/'); }}
          data-testid="admin-logout-button"
          className="flex items-center gap-4 px-4 py-3 min-h-[var(--touch-target-min)] text-destructive hover:text-destructive/80 transition-colors text-nav-item w-full bg-transparent border-none cursor-pointer group relative"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!renderCollapsed && <span>Log Out</span>}
        </button>
      </div>

      {/*
        Sidebar-footer slot — co-branding (Req 8.7, 15.3).
        PR (m.6) Phase 2 — switched from stacked CoBrand to the
        reignara-only watermark variant for a more subtle footer.
        Hidden when collapsed (wordmark won't fit at w-20).
        Theme toggle removed in PR (m.0) — dark mode dropped.
      */}
      <div className="mt-auto p-4 border-t border-border flex flex-col items-center gap-3">
        {!renderCollapsed && <CoBrand variant="watermark" />}
      </div>
    </aside>
  );
}
